import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Box,
  Grid,
  Chip,
  Alert
} from '@mui/material';
import * as Icons from '@mui/icons-material';
import { gradientPrimarySecondary } from '../theme/brandStyles';

const MetierInscription = ({ metier }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Étape 1 - Informations personnelles
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    // Étape 2 - Entreprise
    nomEntreprise: '',
    siret: '',
    ville: '',
    codePostal: '',
    // Étape 3 - Mot de passe
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  if (!metier) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4">Métier non trouvé</Typography>
      </Container>
    );
  }

  const steps = [
    'Vos informations',
    'Votre entreprise',
    'Finalisation'
  ];

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName] || Icons.Build;
    return IconComponent;
  };

  const MetierIcon = getIcon(metier.icon);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
      if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
      if (!formData.email.trim()) {
        newErrors.email = 'L\'email est requis';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email invalide';
      }
      if (!formData.telephone.trim()) {
        newErrors.telephone = 'Le téléphone est requis';
      } else if (!/^[0-9]{10}$/.test(formData.telephone.replace(/\s/g, ''))) {
        newErrors.telephone = 'Numéro de téléphone invalide (10 chiffres)';
      }
    } else if (step === 1) {
      if (!formData.nomEntreprise.trim()) newErrors.nomEntreprise = 'Le nom d\'entreprise est requis';
      if (!formData.siret.trim()) {
        newErrors.siret = 'Le SIRET est requis';
      } else if (!/^[0-9]{14}$/.test(formData.siret.replace(/\s/g, ''))) {
        newErrors.siret = 'SIRET invalide (14 chiffres)';
      }
      if (!formData.ville.trim()) newErrors.ville = 'La ville est requise';
      if (!formData.codePostal.trim()) {
        newErrors.codePostal = 'Le code postal est requis';
      } else if (!/^[0-9]{5}$/.test(formData.codePostal)) {
        newErrors.codePostal = 'Code postal invalide (5 chiffres)';
      }
    } else if (step === 2) {
      if (!formData.password.trim()) {
        newErrors.password = 'Le mot de passe est requis';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === steps.length - 1) {
        handleSubmit();
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    try {
      // TODO: Intégrer avec votre API backend
      console.log('Inscription métier:', metier.id, formData);
      
      // Simuler un délai d'API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Rediriger vers le dashboard ou page de confirmation
      navigate('/dashboard');
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      setErrors({
        submit: 'Une erreur est survenue. Veuillez réessayer.'
      });
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Vos informations personnelles
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ces informations nous permettent de personnaliser votre expérience
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="prenom"
                name="prenom"
                label="Prénom"
                autoComplete="given-name"
                value={formData.prenom}
                onChange={handleChange('prenom')}
                error={!!errors.prenom}
                helperText={errors.prenom}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="nom"
                name="nom"
                label="Nom"
                autoComplete="family-name"
                value={formData.nom}
                onChange={handleChange('nom')}
                error={!!errors.nom}
                helperText={errors.nom}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="email"
                name="email"
                type="email"
                label="Email professionnel"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="telephone"
                name="telephone"
                label="Téléphone"
                placeholder="06 12 34 56 78"
                autoComplete="tel"
                value={formData.telephone}
                onChange={handleChange('telephone')}
                error={!!errors.telephone}
                helperText={errors.telephone}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Votre entreprise {metier.nom.toLowerCase()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Informations légales et localisation
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="nomEntreprise"
                name="nomEntreprise"
                label="Nom de l'entreprise"
                autoComplete="organization"
                value={formData.nomEntreprise}
                onChange={handleChange('nomEntreprise')}
                error={!!errors.nomEntreprise}
                helperText={errors.nomEntreprise}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="siret"
                name="siret"
                label="SIRET"
                placeholder="123 456 789 01234"
                value={formData.siret}
                onChange={handleChange('siret')}
                error={!!errors.siret}
                helperText={errors.siret}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                required
                fullWidth
                id="ville"
                name="ville"
                label="Ville"
                autoComplete="address-level2"
                value={formData.ville}
                onChange={handleChange('ville')}
                error={!!errors.ville}
                helperText={errors.ville}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                required
                fullWidth
                id="codePostal"
                name="codePostal"
                label="Code postal"
                placeholder="75001"
                autoComplete="postal-code"
                value={formData.codePostal}
                onChange={handleChange('codePostal')}
                error={!!errors.codePostal}
                helperText={errors.codePostal}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Sécurisez votre compte
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choisissez un mot de passe robuste pour protéger vos données
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="password"
                name="password"
                type="password"
                label="Mot de passe"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange('password')}
                error={!!errors.password}
                helperText={errors.password || 'Minimum 8 caractères'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                label="Confirmer le mot de passe"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  En vous inscrivant, vous bénéficiez de <strong>14 jours d'essai gratuit</strong> sans 
                  engagement. Aucune carte bancaire requise.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );

      default:
        return 'Étape inconnue';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: gradientPrimarySecondary, py: { xs: 3, sm: 4, md: 8 } }}>
      <Container maxWidth="md">
        {/* Header */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            mb: { xs: 2, sm: 2.5, md: 3 },
            textAlign: 'center',
            borderRadius: 3,
            mx: { xs: 1, sm: 0 }
          }}
        >
          <MetierIcon sx={{ fontSize: { xs: 35, sm: 45, md: 55 }, color: 'primary.main', mb: { xs: 1.5, sm: 2 } }} />
          <Typography 
            variant="h1" 
            fontWeight="bold" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1.3rem', sm: '1.6rem', md: '2rem' },
              lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 },
              mb: { xs: 1, sm: 1.5, md: 2 }
            }}
          >
            Inscription {metier.nom}
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
              lineHeight: { xs: 1.5, sm: 1.6 },
              px: { xs: 1, sm: 2 }
            }}
          >
            Rejoignez {metier.stats.utilisateurs} {metier.nomPluriel} qui optimisent leur activité avec LYS
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, justifyContent: 'center', mt: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
            <Chip 
              icon={<Icons.CheckCircle />} 
              label="14 jours gratuits" 
              color="primary" 
              size="small"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, height: { xs: 26, sm: 28, md: 32 } }}
            />
            <Chip 
              icon={<Icons.CheckCircle />} 
              label="Sans engagement" 
              color="primary" 
              size="small"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, height: { xs: 26, sm: 28, md: 32 } }}
            />
            <Chip 
              icon={<Icons.CheckCircle />} 
              label="Support dédié" 
              color="primary" 
              size="small"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, height: { xs: 26, sm: 28, md: 32 } }}
            />
          </Box>
        </Paper>

        {/* Stepper Form */}
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
          <Stepper activeStep={activeStep} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {errors.submit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.submit}
            </Alert>
          )}

          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: { xs: 3, sm: 4 }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Retour
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              size="large"
              sx={{
                px: 4,
                fontWeight: 'bold',
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {activeStep === steps.length - 1 ? 'Créer mon compte' : 'Suivant'}
            </Button>
          </Box>

          {activeStep === 0 && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Vous avez déjà un compte ?{' '}
                <Button
                  variant="text"
                  onClick={() => navigate('/login')}
                  sx={{ textTransform: 'none' }}
                >
                  Se connecter
                </Button>
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Témoignage rapide */}
        <Box sx={{ mt: 4, textAlign: 'center', color: 'white' }}>
          {metier.temoignages && metier.temoignages[0] && (
            <Paper
              elevation={2}
              sx={{
                p: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 3
              }}
            >
              <Typography variant="h6" color="primary.main" gutterBottom>
                "{metier.temoignages[0].texte}"
              </Typography>
              <Typography variant="body2" color="text.secondary">
                — {metier.temoignages[0].nom}, {metier.temoignages[0].entreprise}
              </Typography>
            </Paper>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default MetierInscription;
